import { Entity } from '@inter-ikea-core/core';
import { PlannerProductComponent, Product } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { getConfigSorting, getProductGroupInformation } from 'ducks/products';
import * as R from 'ramda';
import { ProductGroup, ProductListConfig } from 'rangeConfig/@types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CategoryData, getCategoriesData, sortProductsInCategoriesData } from './getCategoriesData';
import { useWidget } from 'contexts/widget';

export type RenderProps = {
  productListConfig: ProductListConfig;
  categoriesData: CategoryData[];
  selectedGroup?: ProductGroup;
  selectedGroupId: string;
  productsInGroup: Product[];
};

type Props = {
  render: (props: RenderProps) => any;
  productListConfig: ProductListConfig;
};

const ProductsProvider = ({ render, productListConfig }: Props) => {
  const coreApi = useCore();
  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();

  const { configId } = useWidget();
  const sorting = useSelector(getConfigSorting(configId));
  const { visibleProducts, selectedGroupId, productsInGroup } = useSelector(
    getProductGroupInformation
  );

  const selectedGroup = R.find(R.propEq('id', selectedGroupId), productGroups);
  const entitiesInScene = coreApi.entities.getByComponent(PlannerProductComponent.id);

  const applyProductFilters = (entitiesInScene: Entity[]) => {
    if (productListConfig.productFilters && productListConfig.productFilters.length > 0) {
      return productListConfig.productFilters.reduce((acc, filter) => {
        return filter(acc, entitiesInScene, coreApi);
      }, visibleProducts);
    }
    return visibleProducts;
  };

  const filteredVisibleProducts = applyProductFilters(entitiesInScene);

  const categoriesData = useMemo(
    () => getCategoriesData(filteredVisibleProducts, productListConfig),
    [filteredVisibleProducts, productListConfig]
  );

  const sortedCategoriesData = useMemo(
    () =>
      !sorting
        ? categoriesData
        : sortProductsInCategoriesData(categoriesData, productListConfig, sorting.compare ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoriesData, sorting, productListConfig]
  );

  return (
    <>
      {render({
        productListConfig,
        categoriesData: sortedCategoriesData,
        selectedGroup,
        selectedGroupId,
        productsInGroup
      })}
    </>
  );
};

/**
 * HOC:
 * This is a way of reusing the logic in ProductsProvider to provide data
 * to components.
 * @param Component this component will recieve props from ProductsProvider
 * @returns The wrapped component with props
 */
const withProducts = <P extends object>(
  Component: React.FunctionComponent<P>,
  productListConfig: ProductListConfig
): React.FunctionComponent => (p: any) => (
  <ProductsProvider
    render={(props: any) => <Component {...(props as RenderProps)} {...(p as any)} />}
    productListConfig={productListConfig}
  />
);

export default withProducts;
